//@ts-check

import { FEATURES, USER_CLAIMS, capitalize } from '../../common/';
import { shouldBeEnabled } from '../../common/common';
import { NAVIGATION, NAVIGATION_SORTED, navigationToClaim, routeToClaim } from '../../common/navigation-enums';

class AuthorizationService {
	constructor(LocalStorageService) {
		this.LocalStorageService = LocalStorageService;

		this.init();
	}

	init() { }

	get unProtectedRoute() {
		const paymentsAllowed = this.isAuthorized(this.mapPath(FEATURES.PAYMENTS));

		const savedIsHome = this.LocalStorageService.retrieve('isHome');
		const isHome = !!savedIsHome ? JSON.parse(savedIsHome) : false;

		if (!isHome && paymentsAllowed) {
			return FEATURES.PAYMENTS;
		}
		return this._allowedDashboardOrFirstInMenu();
	}

	_allowedDashboardOrFirstInMenu() {
		if (shouldBeEnabled(routeToClaim(NAVIGATION.PropertyList).description)) {
			return FEATURES.DASHBOARD;
		}

		const menu = Object.keys(NAVIGATION)
			.map(key => NAVIGATION[key])
			.map(value => ({ value, claim: routeToClaim(value) }))
			.filter(route => !!route.claim)
			.map(route => ({ value: route.value, order: route.claim.order, description: route.claim.description }));
		const firstAllowed = NAVIGATION_SORTED(menu).find(route => shouldBeEnabled(route.description));

		return (firstAllowed && navigationToClaim(firstAllowed.value)) || FEATURES.DASHBOARD;
	}

	mapPath(path) {
		let mappedPath;
		path = path.toLowerCase();

		if (path.includes(FEATURES.FINANCIAL_SUMMARY)) {
			path = FEATURES.FINANCIAL_SUMMARY;
		}

		switch (path) {
			case FEATURES.LEVY_ROLL:
			case FEATURES.BALANCE_SHEET:
			case FEATURES.INCOME_STATEMENT:
			case FEATURES.CONTRACTORS_ROLL:
			case FEATURES.WAGES_ROLL:
			case FEATURES.FINANCIAL_SUMMARY:
			case FEATURES.CASHFLOW_STATEMENT:
			case FEATURES.CASHFLOW_STATEMENT_NEW:
			case FEATURES.INCOME_VS_BUDGET:
				mappedPath = USER_CLAIMS.VIEW_FINANCIALS;
				break;
			case FEATURES.PAYMENTS:
				mappedPath = USER_CLAIMS.VIEW_PAYMENTS;
				break;
			case FEATURES.PAYMENT_AUTHORISATION:
				mappedPath = USER_CLAIMS.VIEW_PAYMENT_BANDS;
				break;
			case FEATURES.DOCUMENTS:
				mappedPath = USER_CLAIMS.VIEW_BUILDING_DOCUMENTS;
				break;
			case FEATURES.PERSONAL_DOCUMENTS:
				mappedPath = USER_CLAIMS.VIEW_PERSONAL_DOCUMENTS;
				break;
			case FEATURES.DASHBOARD:
				mappedPath = USER_CLAIMS.VIEW_PROPERTY_LIST;
				break;
			case FEATURES.CLEARANCE_CAPTURE:
				mappedPath = USER_CLAIMS.CLEARANCE_CAPTURE;
				break;
			case FEATURES.MY_PROFILE:
				mappedPath = FEATURES.MY_PROFILE;
				break;
			case FEATURES.ACCOUNT_HISTORY:
				mappedPath = USER_CLAIMS.VIEW_ACCOUNTS_HISTORY;
				break;
			case FEATURES.UTILITY:
				mappedPath = FEATURES.UTILITY;
				break;
			case FEATURES.DOCUMENTS_APPROVAL:
				mappedPath = USER_CLAIMS.VIEW_DOCUMENTS_APPROVAL;
				break;
			case FEATURES.RENTALS_CAPTURE:
				mappedPath = USER_CLAIMS.RENTALS_CAPTURE;
				break;
			case FEATURES.AUDITS_CAPTURE:
				mappedPath = USER_CLAIMS.AUDITS_CAPTURE;
				break;
			case FEATURES.AUDITORS_DASHBOARD:
				mappedPath = USER_CLAIMS.AUDITORS_DASHBOARD;
				break;
			case FEATURES.DEBTOR_AGE_ANALYSIS:
				mappedPath = USER_CLAIMS.DEBTOR_AGE_ANALYSIS;
				break;			
			case FEATURES.SHOW_FIND_A_CONTRACTOR:
					mappedPath = USER_CLAIMS.SHOW_FIND_A_CONTRACTOR;
					break;
		}

		return mappedPath;
	}

	/**
	 * Checks whether a user should have access or not
	 * @param {string} claim Section that a user is operating/viewing
	 * @returns {boolean} true or false
	 */
	isAuthorized(claim = '') {
		claim = claim.toLowerCase();
		let shouldAuthorize = false;

		const userProfile = this.LocalStorageService.retrieve('userProfile');
		let selectedBuildingRef = this.LocalStorageService.retrieve('selectedBuildingRef');

		if (!!!selectedBuildingRef && !!userProfile) {
			if (!!userProfile.BuildingProfiles && userProfile.BuildingProfiles.length > 0) {
				selectedBuildingRef = userProfile.BuildingProfiles[0].SchemeReference;
			}
		}

		if (selectedBuildingRef && userProfile) {
			if (typeof selectedBuildingRef === 'object') {
				selectedBuildingRef = selectedBuildingRef.SchemeReference;
			}

			if (selectedBuildingRef) {
				const selectedBuilding = userProfile.BuildingProfiles.find((buildingProf) => {
					const buildingRef = buildingProf.SchemeReference.toLowerCase().trim();
					if (buildingRef === selectedBuildingRef.toLowerCase().trim()) {
						return true;
					}
				});

				if (selectedBuilding) {
					if (
						//claim === FEATURES.MY_PROFILE ||
						claim === FEATURES.DASHBOARD ||
						claim === '' ||
						claim === '/'
					) {
						return true;
					} else if (
						claim === USER_CLAIMS.VIEW_PROPERTY_LIST.toLowerCase() ||
						claim === FEATURES.MY_PROFILE.toLowerCase()
					) {
						const userRole = selectedBuilding.Role.find(
							(role) => role === 'Owner' /* || role === 'Chairman'*/
						);
						shouldAuthorize = !!userRole;
					} else {
						const hasClaim = selectedBuilding.BuildingClaimMapped.find((desc) => {
							const results = desc.Description.toLowerCase().trim() === claim.toLowerCase().trim();
							return results;
						});

						if (!hasClaim) {
							const owner = selectedBuilding.Role.find((role) => role.toLowerCase() === 'owner');
							if (!!owner && capitalize(claim) === USER_CLAIMS.VIEW_PERSONAL_DOCUMENTS) {
								shouldAuthorize = true;
							}
						} else {
							shouldAuthorize = !!hasClaim;
						}
					}
				}
			}
		}

		return shouldAuthorize;
	}

	mapPageToFeature(page) {
		let feature;
		page = capitalize(page);

		switch (page) {
			case USER_CLAIMS.MY_PROFILE:
				feature = FEATURES.MY_PROFILE;
				break;
			case USER_CLAIMS.VIEW_LEVY_ROLL:
				feature = FEATURES.LEVY_ROLL;
				break;
			case USER_CLAIMS.VIEW_PAYMENTS:
				feature = FEATURES.PAYMENTS;
				break;
			case USER_CLAIMS.REGISTER_TENANT:
				feature = FEATURES.TENANT;
				break;
			case USER_CLAIMS.VIEW_ACCOUNTS_HISTORY:
				feature = FEATURES.ACCOUNT_HISTORY;
				break;
			case USER_CLAIMS.VIEW_BALANCE_SHEET:
				feature = FEATURES.BALANCE_SHEET;
				break;
			case USER_CLAIMS.VIEW_BUILDING_DOCUMENTS:
				feature = FEATURES.DOCUMENTS;
				break;
			case USER_CLAIMS.VIEW_CASHFLOW_STATEMENT:
				feature = FEATURES.CASHFLOW_STATEMENT;
				break;			
			case USER_CLAIMS.VIEW_CASHFLOW_STATEMENT_NEW:
				feature = FEATURES.CASHFLOW_STATEMENT_NEW;
				break;
			case USER_CLAIMS.VIEW_CONTRACTORS_ROLL:
				feature = FEATURES.CONTRACTORS_ROLL;
				break;
			case USER_CLAIMS.VIEW_WAGES_ROLL:
				feature = FEATURES.WAGES_ROLL;
				break;
			case USER_CLAIMS.VIEW_FINANCIALS:
				feature = FEATURES.FINANCIAL_SUMMARY;
				break;
			case USER_CLAIMS.VIEW_INCOME_STATEMENT:
				feature = FEATURES.INCOME_STATEMENT;
				break;
			case USER_CLAIMS.VIEW_INCOME_VS_BUDGET:
				feature = FEATURES.INCOME_VS_BUDGET;
				break;
			case USER_CLAIMS.VIEW_PAYMENT_BANDS:
			case USER_CLAIMS.VIEW_CONTRACTUAL_PAYMENTS:
				feature = FEATURES.PAYMENT_AUTHORISATION;
				break;
			case USER_CLAIMS.VIEW_PERSONAL_DOCUMENTS:
				feature = FEATURES.PERSONAL_DOCUMENTS;
				break;
			case USER_CLAIMS.UTILITY:
				feature = FEATURES.UTILITY;
				break;
			case USER_CLAIMS.VIEW_DOCUMENTS_APPROVAL:
				feature = FEATURES.DOCUMENTS_APPROVAL;
				break;
			case USER_CLAIMS.DEBTOR_AGE_ANALYSIS:
				feature = FEATURES.DEBTOR_AGE_ANALYSIS;
				break;
			case USER_CLAIMS.SHOW_FIND_A_CONTRACTOR:
				feature = FEATURES.SHOW_FIND_A_CONTRACTOR;
				break;
		}

		return feature;
	}

	_getSelectedBuildingRef() {
		let selectedBuildingRef = this.LocalStorageService.retrieve('selectedBuildingRef');

		if (selectedBuildingRef && typeof selectedBuildingRef === 'object')
			selectedBuildingRef = selectedBuildingRef.SchemeReference;

		return selectedBuildingRef;
	}
}

export default /* @ngInject */ (LocalStorageService) => new AuthorizationService(LocalStorageService);
